import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { LoginIdentifierField, type LoginMode } from '../components/LoginIdentifierField'
import { apiLogin } from '../lib/api'
import { isLideranca, setAuth, getStoredUser } from '../lib/auth'
import { PHONE_COUNTRIES, type PhoneCountry } from '../lib/phoneCountries'
import { pubImg } from '../lib/pubImg'

function safeInternalRedirect(raw: string | null): string | null {
  if (!raw) return null
  const path = raw.trim()
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('://')) return null
  return path
}

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = safeInternalRedirect(searchParams.get('redirect'))
  const existing = getStoredUser()
  const [mode, setMode] = useState<LoginMode>('phone')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [country, setCountry] = useState<PhoneCountry>(PHONE_COUNTRIES[0])
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (existing && isLideranca(existing)) {
    return <Navigate to={redirectTo || '/hub/inicio'} replace />
  }

  function switchMode(next: LoginMode) {
    setMode(next)
    setError('')
    if (next === 'phone') setEmail('')
    else setPhone('')
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const identifier = mode === 'email' ? email : phone
      const res = await apiLogin(identifier, password, { mode })
      const data = res.data
      if (!data?.token || !data.user) {
        throw new Error(res.error || res.message || 'Credenciais inválidas')
      }
      if (data.user.id_permissao !== 1) {
        window.location.href = data.redirect || '/dashboard'
        return
      }
      setAuth(data.token, {
        id: data.user.id,
        nome: data.user.nome,
        id_permissao: data.user.id_permissao,
        campanha_id: data.campanha.id,
        campanha_nome: data.campanha.nome,
      })
      navigate(redirectTo || '/hub/inicio', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no login')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="hub-login-shell">
      <div className="hub-login-shell__pattern" aria-hidden="true">
        <img src={pubImg('images/modern.svg')} alt="" />
      </div>

      <div className="hub-login-form-col">
        <div className="hub-login-card">
          <a href="https://eleitorweb.com.br" className="hub-login-logo" title="EleitorWEB">
            <img
              src={pubImg('images/eleitorweb-300.png')}
              alt="EleitorWEB"
              onError={(e) => {
                e.currentTarget.src = pubImg('images/logo-light.png')
              }}
            />
          </a>

          <p className="bp-card__eyebrow">Base Política</p>
          <h1 className="hub-login-title">Entrar</h1>
          <p className="bp-muted hub-login-subtitle">
            Telefone ou e-mail. Liderança entra no Hub; gestores seguem ao painel.
          </p>

          <form className="bp-stack hub-login-form" onSubmit={onSubmit}>
            <LoginIdentifierField
              mode={mode}
              onModeChange={switchMode}
              phone={phone}
              onPhoneChange={setPhone}
              email={email}
              onEmailChange={setEmail}
              country={country}
              onCountryChange={setCountry}
            />

            <label className="bp-field">
              <span>Senha</span>
              <div className="hub-login-password">
                <input
                  className="bp-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="hub-login-password__toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              <Link to="/forgot-password" className="hub-login-link-forgot">
                Esqueceu a senha?
              </Link>
            </label>

            {error ? <p className="bp-error">{error}</p> : null}

            <button className="bp-btn bp-btn--primary" type="submit" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <a
            href="https://eleitorweb.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="bp-btn hub-login-hire"
          >
            Contrate o EleitorWEB
          </a>

          <p className="bp-muted hub-login-classic">
            <a href="/login?classic=1">Entrar pelo site clássico</a>
          </p>
        </div>
      </div>

      <aside className="hub-login-promo" aria-label="Sobre o Hub">
        <img
          src={pubImg('images/logo-dark.png')}
          alt="EleitorWEB"
          className="hub-login-promo__logo"
          onError={(e) => {
            e.currentTarget.src = pubImg('images/eleitorweb-200.png')
          }}
        />
        <img
          src={pubImg('images/telas/ew-tela1.jpg')}
          alt="Prévia do sistema"
          className="hub-login-promo__shot"
        />
        <h2 className="hub-login-promo__title">Hub da liderança</h2>
        <p className="hub-login-promo__desc">
          Convide, acompanhe a rede, missões, notícias e pontuação — no celular, com a mesma
          base do painel.
        </p>
        <div className="hub-login-promo__grid">
          <div className="hub-login-promo__card">
            <strong>Rede</strong>
            <span>Convidados e árvore</span>
          </div>
          <div className="hub-login-promo__card">
            <strong>Campanha</strong>
            <span>Missões e conteúdo</span>
          </div>
          <div className="hub-login-promo__card">
            <strong>Engajamento</strong>
            <span>Ranking e medalhas</span>
          </div>
        </div>
      </aside>
    </div>
  )
}
